import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import { current } from "immer";
import type { OfferDetailResponse } from "@codway-dev/shared";
import { OfferService } from "../services/offer.service";
import { isDeepEqual } from "../utils/deep-equal";
import {
  getDefaultQuantityTiersState,
  getDefaultNewTier,
  offerToState,
  stateToCreateRequest,
  stateToUpdateRequest,
  type QuantityTiersEditorState,
  type QuantityTierEditorTier,
} from "./quantity-tiers-editor.mapping";

// ─── Types ────────────────────────────────────────────────────────────────────

interface QuantityTiersEditorStoreState {
  config: QuantityTiersEditorState;
  offerId: string | null;
  originalConfig: OfferDetailResponse["config"] | null;
  initialConfig: QuantityTiersEditorState | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface QuantityTiersEditorActions {
  initNew: () => void;
  loadFromBackend: (id: string) => Promise<void>;
  updateField: <K extends keyof QuantityTiersEditorState>(
    key: K,
    value: QuantityTiersEditorState[K]
  ) => void;
  addTier: () => void;
  removeTier: (index: number) => void;
  updateTier: <K extends keyof QuantityTierEditorTier>(
    index: number,
    key: K,
    value: QuantityTierEditorTier[K]
  ) => void;
  save: () => Promise<string>;
  discardChanges: () => void;
  reset: () => void;
}

type QuantityTiersEditorStore = QuantityTiersEditorStoreState &
  QuantityTiersEditorActions;

// ─── Selectors ───────────────────────────────────────────────────────────────

export const selectQuantityTiersIsDirty = (state: QuantityTiersEditorStore) => {
  if (state.initialConfig === null) return false;
  return !isDeepEqual(state.config, state.initialConfig);
};

// ─── Store ───────────────────────────────────────────────────────────────────

const getInitialState = (): QuantityTiersEditorStoreState => ({
  config: getDefaultQuantityTiersState(),
  offerId: null,
  originalConfig: null,
  initialConfig: null,
  isLoading: false,
  isSaving: false,
  error: null,
});

export const useQuantityTiersEditorStore = create<QuantityTiersEditorStore>()(
  devtools(
    immer((set, get) => ({
      ...getInitialState(),

      initNew: () =>
        set((state) => {
          const fresh = getDefaultQuantityTiersState();
          state.config = fresh;
          state.offerId = null;
          state.originalConfig = null;
          state.initialConfig = fresh;
          state.isLoading = false;
          state.isSaving = false;
          state.error = null;
        }),

      loadFromBackend: async (id) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const offer = await OfferService.get(id);
          const hydrated = offerToState(offer);
          set((state) => {
            state.config = hydrated;
            state.offerId = offer.id;
            state.originalConfig = offer.config;
            state.initialConfig = hydrated;
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error
                ? err.message
                : "No se pudo cargar la oferta";
            state.isLoading = false;
          });
          throw err;
        }
      },

      updateField: (key, value) =>
        set((state) => {
          state.config[key] = value as never;
        }),

      addTier: () =>
        set((state) => {
          state.config.tiers.push(getDefaultNewTier());
        }),

      removeTier: (index) =>
        set((state) => {
          if (state.config.tiers.length <= 1) return;
          state.config.tiers.splice(index, 1);
        }),

      updateTier: (index, key, value) =>
        set((state) => {
          if (index < 0 || index >= state.config.tiers.length) return;
          state.config.tiers[index][key] = value as never;
          if (key === "isDefault" && value === true) {
            state.config.tiers.forEach((t, i) => {
              if (i !== index) t.isDefault = false;
            });
          }
        }),

      save: async () => {
        set((state) => {
          state.isSaving = true;
          state.error = null;
        });

        try {
          const s = get();
          let savedId: string;

          if (s.offerId === null) {
            const created = await OfferService.create(
              stateToCreateRequest(s.config)
            );
            savedId = created.id;
          } else {
            const updated = await OfferService.update(
              s.offerId,
              stateToUpdateRequest(s.config, s.originalConfig)
            );
            savedId = updated.id;
          }

          set((state) => {
            state.offerId = savedId;
            state.initialConfig = current(state.config);
            state.isSaving = false;
          });

          return savedId;
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Error al guardar la oferta";
          set((state) => {
            state.isSaving = false;
            state.error = message;
          });
          throw new Error(message);
        }
      },

      discardChanges: () =>
        set((state) => {
          if (!state.initialConfig) return;
          state.config = state.initialConfig;
        }),

      reset: () =>
        set((state) => {
          Object.assign(state, getInitialState());
        }),
    })),
    { name: "QuantityTiersEditorStore" }
  )
);
